import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  errorToast,
  handleError,
  parseAndFormateDate,
} from "../../utils/common";
import useAxiosInstance from "../../hooks/useAxiosInstance";
import { useEffect, useState } from "react";
import { API_RESULT, API_VERSION } from "../../enums/common.enum";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const DataRow = ({ label, value, onInfoClick }: any) => (
  <Grid container spacing={1} sx={{ mb: 1 }} alignItems="center">
    <Grid item xs={5}>
      <Box display="flex" alignItems="center">
        <Typography
          variant="body1"
          component="span"
          sx={{
            textTransform: "none",
            wordBreak: "break-word",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
          }}
        >
          {label}:
        </Typography>
      </Box>
    </Grid>
    <Grid item xs={7}>
      <Box display="flex" alignItems="center">
        {typeof value === "string" || typeof value === "number" ? (
          <Typography
            variant="body1"
            sx={{
              textTransform: "none",
              wordBreak: "break-word",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            }}
          >
            {value}
          </Typography>
        ) : (
          value
        )}
        {onInfoClick && (
          <IconButton
            size="small"
            sx={{
              ml: 0.5,
              backgroundColor: "primary.main",
              color: "#fff",
              height: 20,
              width: 20,
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
            aria-label={`${label} info`}
            onClick={onInfoClick}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Grid>
  </Grid>
);

export const PurchaseDetails = ({ open, handleClose, activeInvId }: any) => {
  const privateInstance = useAxiosInstance();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [infoContent, setInfoContent] = useState<
    { label: string; value: string }[] | null
  >(null);

  useEffect(() => {
    if (!activeInvId) return;

    const getSinglePurchaseData = async () => {
      setLoading(true);
      try {
        const result = await privateInstance.get(
          `${API_VERSION.V3}/suppliers/purchases?purchase_id=${activeInvId}`
        );
        if (result?.data?.status === API_RESULT.SUCCESS) {
          setData(result.data.response);
        } else {
          errorToast(result.data.message);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

    getSinglePurchaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeInvId]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="purchase-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Typography variant="h6" component="div">
            Purchase Data
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent dividers>
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <>
              {data && (
                <>
                  <Box sx={{ mt: 1 }}>
                    <DataRow label="Invoice ID" value={data?.invoice_id} />
                    {/* <DataRow label="Supplier ID" value={data?.supplier_id} /> */}
                    <DataRow
                      label="Supplier Name"
                      value={data?.supplier_name}
                    />

                    <DataRow label="Invoice Date" value={data?.invoice_date} />
                    <DataRow label="Due Date" value={data?.due_date} />
                    {/* <DataRow label="Location ID" value={data?.location_id} /> */}
                    <DataRow
                      label="Invoice Amount"
                      value={`₹ ${data?.invoice_amount}`}
                    />
                    <DataRow
                      label="Payable Amount"
                      value={`₹ ${data?.payable_amount}`}
                    />
                    {data?.payment_status != "processing" && (
                      <DataRow
                        label="Payment Status"
                        value={
                          <Typography
                            component={
                              typeof data?.payment_status === "string"
                                ? "p"
                                : "div"
                            }
                            sx={{
                              textTransform: "capitalize",
                              color:
                                data?.payment_status === "paid"
                                  ? "#1e8e3e"
                                  : data?.payment_status === "processing"
                                  ? "#f9a825"
                                  : data?.payment_status === "failed"
                                  ? "#d32f2f"
                                  : "text.primary",
                              fontWeight: "bold",
                            }}
                          >
                            {data?.payment_status}
                          </Typography>
                        }
                      />
                    )}
                    <DataRow
                      label="Beneficiary"
                      value={data?.payment_mode}
                      onInfoClick={
                        data?.beneficiary_details
                          ? () => {
                              const isUPI =
                                data.payment_mode?.toLowerCase() === "upi";
                              const beneficiary = data.beneficiary_details;

                              const details = isUPI
                                ? [
                                    {
                                      label: "UPI ID",
                                      value: beneficiary.upi_id,
                                    },
                                  ]
                                : [
                                    {
                                      label: "Bank Name",
                                      value: beneficiary.bank,
                                    },
                                    {
                                      label: "Account No.",
                                      value: beneficiary.account_no,
                                    },
                                    {
                                      label: "IFSC Code",
                                      value: beneficiary.ifsc_code,
                                    },
                                    {
                                      label: "Account Name",
                                      value: beneficiary.account_name,
                                    },
                                    {
                                      label: "Account Type",
                                      value: beneficiary.account_type,
                                    },
                                  ];

                              setInfoContent(details);
                              setShowInfoDialog(true);
                            }
                          : null
                      }
                    />
                    <DataRow
                      label="Modified At"
                      value={String(parseAndFormateDate(data?.modified_at))}
                    />
                    {data?.payment_status === "processing" ? (
                      <Typography sx={{ mt: 1.5 }} variant="subtitle2">
                        <strong>Note:</strong> Payment received on{" "}
                        <strong>
                          {data?.p_2_m?.txn_date} (Bank Ref:{" "}
                          {data?.p_2_m?.bank_ref})
                        </strong>{" "}
                        from VPA <strong>{data?.p_2_m?.payer_vpa}</strong>.
                      </Typography>
                    ) : null}
                    {data?.payment_status === "paid" ? (
                      <>
                        <Divider sx={{ mb: 1.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Payment Details
                          </Typography>
                        </Divider>
                        <DataRow
                          label="Payment UTR"
                          value={data?.payment_UTR}
                        />
                        <DataRow
                          label="Payment At"
                          value={String(
                            parseAndFormateDate(data?.settlement_at)
                          )}
                        />
                        <DataRow
                          label="Settlement Mode"
                          value={data?.settlement_mode}
                        />
                        <DataRow
                          label="Settlement Amount"
                          value={`₹ ${data?.settlement_amount}`}
                        />
                        {data?.hasOwnProperty("beneficiery_details") ? (
                          <>
                            <Divider sx={{ my: 1.5 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Beneficiary Details
                              </Typography>
                            </Divider>
                            {data?.payment_mode?.toLowerCase() === "upi" ? (
                              <DataRow
                                label="Payment UTR"
                                value={data?.beneficiery_details?.upi_id}
                              />
                            ) : (
                              <>
                                <DataRow
                                  label="Account Name"
                                  value={
                                    data?.beneficiery_details?.account_name
                                  }
                                />
                                <DataRow
                                  label="Account No."
                                  value={data?.beneficiery_details?.account_no}
                                />
                                <DataRow
                                  label="IFSC Code"
                                  value={data?.beneficiery_details?.ifsc_code}
                                />
                              </>
                            )}
                          </>
                        ) : null}
                        {data?.coins_earned && (
                          <Alert sx={{ mt: 1.5 }} color="success">
                            You’ve earned {data?.coins_earned} coins on this
                            payment.
                          </Alert>
                        )}
                      </>
                    ) : null}
                  </Box>
                  {data?.invoice_urls?.length ? (
                    <Box mt={1.5}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Invoice Images:
                      </Typography>
                      <Box mt={0.5} display="flex" gap={1}>
                        {data?.invoice_urls?.map((img: string) => {
                          return (
                            <img
                              key={img}
                              src={img}
                              alt="img"
                              width={100}
                              height={100}
                              style={{ borderRadius: 8 }}
                              //onClick={() => window.open(img, "_blank")}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  ) : null}
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showInfoDialog} onClose={() => setShowInfoDialog(false)}>
        <DialogTitle>
          Beneficiary Info
          <IconButton
            aria-label="close"
            onClick={() => setShowInfoDialog(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {infoContent?.map((row, idx) => (
            <DataRow key={idx} label={row.label} value={row.value} />
          ))}
        </DialogContent>
      </Dialog>
    </>
  );
};
